import React from "react";
import styled from "styled-components";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import { ScrollReveal } from "features/whop/whopLanding/components/animations.tsx";

interface RoleToggleProps {
  role: Role;
  onRoleChange: (role: Role) => void;
}

export const RoleToggle: React.FC<RoleToggleProps> = ({
  role,
  onRoleChange,
}) => {
  return (
    <SectionOuter>
      <Section>
        <ScrollReveal>
          <Label>Choose Your Role</Label>
          <ToggleWrapper role="group" aria-label="Select your role">
            <ToggleButton
              $active={role === "member"}
              onClick={() => onRoleChange("member")}
              aria-pressed={role === "member"}
            >
              <UserIcon />
              I'm a Group Member
            </ToggleButton>
            <ToggleButton
              $active={role === "admin"}
              onClick={() => onRoleChange("admin")}
              aria-pressed={role === "admin"}
            >
              <CrownIcon />I Run a Trading Group
            </ToggleButton>
          </ToggleWrapper>
        </ScrollReveal>
      </Section>
    </SectionOuter>
  );
};

const UserIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CrownIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 4l3 12h14l3-12-6 5-4-8-4 8-6-5z" />
    <path d="M5 20h14" />
  </svg>
);

const SectionOuter = styled.div`
  background: ${(p) => (p.theme.isDarkMode ? "#0d1117" : "#F8FAFC")};
  border-top: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  border-bottom: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
`;

const Section = styled.section`
  padding: 32px 16px 48px;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  @media (min-width: 768px) {
    padding: 40px 24px 56px;
  }
`;

const Label = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  margin: 0 0 20px;
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin: 0 0 32px;
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  padding: 6px;
  gap: 6px;
  background: ${(p) => (p.theme.isDarkMode ? "#111827" : "#F3F4F6")};
  border: 2px solid ${(p) => (p.theme.isDarkMode ? "#1F2937" : "#E5E7EB")};
  @media (min-width: 480px) {
    display: inline-flex;
    flex-direction: row;
    padding: 8px;
    gap: 8px;
  }
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 14px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  background: ${(p) => (p.$active ? "#2563EB" : "transparent")};
  color: ${(p) =>
    p.$active ? "#fff" : p.theme.isDarkMode ? "#9CA3AF" : "#4B5563"};
  box-shadow: ${(p) =>
    p.$active ? "0 4px 20px rgba(37, 99, 235, 0.25)" : "none"};
  &:hover {
    background: ${(p) =>
      p.$active
        ? "#2563EB"
        : p.theme.isDarkMode
        ? "rgba(255,255,255,0.05)"
        : "#E5E7EB"};
    color: ${(p) =>
      p.$active ? "#fff" : p.theme.isDarkMode ? "#ffffff" : "#111827"};
  }
  @media (min-width: 480px) {
    width: auto;
    min-width: 220px;
    padding: 16px 32px;
    font-size: 1.125rem;
    gap: 12px;
  }
`;
